// ============================================
// Leka AI (tryleka.com)
//
// World Dashboard + Character Profiles + Voice Calls
// ============================================
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router'
import { Suspense, lazy, useEffect, useState } from 'react'
import { useAuth } from '@/shared/hooks/useAuth'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'

// ── Grain Overlay (Lore-style film grain texture) ──────────────────────────
function GrainOverlay() {
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        opacity: reducedMotion ? 0 : 0.04,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
        zIndex: 9998,
        backgroundImage: "url('/noise.png')",
        backgroundRepeat: 'repeat',
      }}
    />
  )
}

// Eagerly loaded
import { WorldHome } from '@/routes/WorldHome'
import { Login } from '@/routes/Login'

// Lazy load
const CallImage = lazy(() => import('@/routes/CallImage').then((m) => ({ default: m.CallImage })))
const Embed = lazy(() => import('@/routes/Embed').then((m) => ({ default: m.Embed })))
const JoinCall = lazy(() => import('@/routes/JoinCall').then((m) => ({ default: m.JoinCall })))
const CharProfile = lazy(() => import('@/routes/CharProfile').then((m) => ({ default: m.CharProfile })))
const SurfaceEntry = lazy(() => import('@/routes/SurfaceEntry').then((m) => ({ default: m.SurfaceEntry })))
const Privacy = lazy(() => import('@/routes/Privacy').then((m) => ({ default: m.Privacy })))
const Terms = lazy(() => import('@/routes/Terms').then((m) => ({ default: m.Terms })))
const LineAttach = lazy(() => import('@/routes/LineAttach').then((m) => ({ default: m.LineAttach })))
const DevCallHarness = import.meta.env.DEV
  ? lazy(() => import('@/routes/DevCallHarness').then((m) => ({ default: m.DevCallHarness })))
  : null
// Watch removed — Live Watch deprecated 2026-03-11
// const Social = lazy(() => import('@/routes/Social').then((m) => ({ default: m.Social }))) // removed: NW Social killed

// ── Helpers ────────────────────────────────────────────────────────────────

function CallWithId() {
  const { charId } = useParams<{ charId: string }>()
  if (!charId || !/^[a-z0-9_-]+$/i.test(charId)) return <Navigate to="/" replace />
  return <CallImage charId={charId} />
}

/** /@mia → charId="mia" | /@mia?mode=image → CallImage */
function CharProfileRouter() {
  const { atCharId } = useParams<{ atCharId?: string }>()
  const [searchParams] = useSearchParams()
  const charId = atCharId?.startsWith('@') ? atCharId.slice(1) : atCharId

  if (!charId || !/^[a-z0-9_-]+$/i.test(charId)) {
    return <Navigate to="/" replace />
  }

  if (charId === 'mira' || charId === 'mira-jp') {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('call', '1')
    nextParams.set('lang', charId === 'mira-jp' ? 'ja' : 'en')
    return <Navigate to={`/${nextParams.toString() ? `?${nextParams.toString()}` : ''}`} replace />
  }

  if (searchParams.get('mode') === 'image' || searchParams.get('call') === '1') {
    const guestUserId = searchParams.get('guestUserId') || undefined
    return <CallImage charId={charId} guestUserId={guestUserId} />
  }

  return <CharProfile charId={charId} />
}

function PageLoader() {
  return (
    <div className="flex-1 flex items-center justify-center bg-nw-void">
      <div className="w-8 h-8 border-2 border-nw/30 border-t-nw rounded-full animate-spin" />
    </div>
  )
}

// Auth guard
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <PageLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

// ── Routes ────────────────────────────────────────────────────────────────

export function App() {
  return (
    <ErrorBoundary>
      <GrainOverlay />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<WorldHome />} />
            <Route path="/creator/ship-this-week" element={<SurfaceEntry />} />
            <Route path="/study/finals-reset" element={<SurfaceEntry />} />
            <Route path="/weight-loss/restart-after-binge" element={<SurfaceEntry />} />
            <Route path="/inner-life/daily-reset" element={<SurfaceEntry />} />
            <Route path="/soka/daily-reset" element={<SurfaceEntry />} />
            <Route path="/money/spend-reset" element={<SurfaceEntry />} />
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/line/connect" element={<LineAttach />} />
            {/* <Route path="/social" element={<Social />} /> */}
            {/* /watch removed — Live Watch deprecated 2026-03-11 */}
            <Route path="/:atCharId" element={<CharProfileRouter />} />

            <Route
              path="/call/:charId"
              element={
                <RequireAuth>
                  <CallWithId />
                </RequireAuth>
              }
            />

            {/* チャネル横断ワンタイム通話リンク（ログイン不要） */}
            <Route path="/join/:charId" element={<JoinCall />} />

            {/* 外部埋め込み用（認証不要・ゲストモード） */}
            <Route path="/embed/:charId" element={<Embed />} />

            {/* Dev UI harness — no API deps, pure component testing */}
            {DevCallHarness ? <Route path="/dev/call" element={<DevCallHarness />} /> : null}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
